import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import ArrowIcon from "../../_assets/vectors/left-arrow-icon.svg";
import { ActionModal } from "../../_components/action-modal.component";
import I18 from "../../_components/atoms/i18";
import { Button } from "../../_components/button.component";
import EmbeddedSectionView from "../../_components/embedded-section-view.component";
import { ErrorDiv } from "../../_components/error.component";
import { Checkbox } from "../../_components/form-controls/checkbox.component";
import { TextInput } from "../../_components/text-input.component";
import { ValidationErrorDiv } from "../../_components/validation-error.component";
import { history } from "../../_config";
import { EmbeddedSectionService } from "../../_service/embedded-section.service";
import { COMMUNITY_SETTING_EMBEDDED_SECTION_PAGE_ROUTE } from "./embedded-sections.page";
import { useAppService } from "../../hooks/use-app-service";

export const COMMUNITY_SETTING_EMBEDDED_SECTION_EDIT_PAGE_ROUTE =
  "/settings/embedded-sections/:sectionId";

export const getCommunitySettingEmbeddedSectionEditPageRoute = (sectionId) =>
  `/settings/embedded-sections/${sectionId ?? "new"}`;

function CommunitySettingEmbeddedSectionEditPageComponent({
  user,
  community,
  addToast = (e) => {},
}) {
  let { sectionId: selectedEmbeddedSectionId } = useParams();
  if (selectedEmbeddedSectionId === "new") {
    selectedEmbeddedSectionId = null;
  }

  const { analyticsService } = useAppService();

  const [name, setName] = useState("");
  const [embedCode, setEmbedCode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [slug, setSlug] = useState(undefined);

  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const communityId = community?.id ?? null;
  const hasAuthTokenEmbedEnabled = community?.configuration?.hasAuthTokenEmbedEnabled ?? false;

  const handleSubmit = () => {
    // validate name
    if (!name || name.length < 2) {
      return setErrors({
        name: ["Name invalid!"],
      });
    }

    setIsUpdateInProgress(true);
    // if selectedEmbeddedSectionId
    if (selectedEmbeddedSectionId && selectedEmbeddedSectionId !== "new") {
      EmbeddedSectionService.updateEmbeddedSection(user, community, {
        id: selectedEmbeddedSectionId,
        name,
        embedCode,
        slug,
        isActive,
      })
        .then(({ embeddedSection }) => {
          addToast("Embedded channel updated!");
          analyticsService.track("update-embed-channel", {
            visibility: isActive
          })
        })
        .catch((error) => {
          console.log(error);
          addToast(error.message, "", ToastTypes.danger);
          if (error?.response?.data?.errors) {
            setErrors(error.response.data.errors);
          }
        });
    } else {
      EmbeddedSectionService.createEmbeddedSection(user, community, {
        name,
        embedCode,
        isActive,
      })
        .then(({ embeddedSection }) => {
          addToast("Embedded channel created!");
          analyticsService.track("save-new-embedded-channel", {
            visibility: isActive
          })
          history.push(
            getCommunitySettingEmbeddedSectionEditPageRoute(embeddedSection.id)
          );
        })
        .catch((error) => {
          console.log({ error });
          addToast(error.message, "", ToastTypes.danger);
          if (error?.response?.data?.errors) {
            setErrors(error.response.data.errors);
          }
        });       
    }
  };

  const deleteSectionTrigger = () => {
    setIsDeleteModalVisible(true);
  };

  const deleteSection = () => {
    EmbeddedSectionService.deleteEmbeddedSection(
      community.id,
      selectedEmbeddedSectionId
    )
      .then(({ embeddedSection }) => {
        // remove section matching the id from sections
        addToast("Embedded channel deleted successfully");
        history.push(COMMUNITY_SETTING_EMBEDDED_SECTION_PAGE_ROUTE);
      })
      .catch((error) => {
        console.log({ error });
        addToast("Error deleting embedded channel", "", ToastTypes.danger);
      });
      analyticsService.track("delete-embed-channel")
  };

  // load needed embedded section modal
  useEffect(() => {
    if (selectedEmbeddedSectionId) {
      // load the section details
      EmbeddedSectionService.getEMbeddedSectionDetailByIdForAdmin(
        communityId,
        selectedEmbeddedSectionId
      )
        .then(({ embeddedSection }) => {
          setName(embeddedSection.name);
          setEmbedCode(embeddedSection.embedCode);
          setIsActive(embeddedSection.isActive);
          setSlug(embeddedSection.slug);
        })
        .catch((error) => {
          console.log({ error });
        });
    } else {
      // clear things out
      setName("");
      setEmbedCode("");
      setIsActive(true);
    }
  }, [communityId, selectedEmbeddedSectionId]);

  return (
    <div className="Card rounded shadow theme-bg-surface">
      {/* headers */}
      <div className="pl-4 pr-2 py-4  border-b theme-border-default flex items-center justify-between">
        <span className="font-bold theme-text-heading-2 flex items-center">
          <span
            className="cursor-pointer"
            onClick={() => {
              history.push(COMMUNITY_SETTING_EMBEDDED_SECTION_PAGE_ROUTE);
            }}>
            <img src={ArrowIcon} alt="arrow theme-text-primary" />
          </span>
          <span className="ml-2">
            <I18>Create Embedded channel</I18>
          </span>
        </span>
        {/* create new key button */}
        {selectedEmbeddedSectionId ? (
          <Button
            label="Delete"
            className="mx-2"
            flat
            btnColor="red"
            onClick={deleteSectionTrigger}
          />
        ) : (
          <></>
        )}
      </div>
      {/* body */}
      <div className="card p-5 pt-0 theme-text-heading-2 mt-2 ">
        {/* Section Name */}
        <div>
          <div className="font-semibold theme-text-heading-2 mt-4">
            <I18>Channel Name</I18>
          </div>
          <TextInput
            placeholder="Enter here"
            validator={(value) => {
              if (value.length < 2) {
                return "Name invalid!";
              }
            }}
            defaultValue={name}
            className="py-2 focus:outline-none theme-bg-default"
            onChange={(value) => {
              setName(value);
              // set address
            }}
          />
          <ValidationErrorDiv errorKey="name" errors={errors} />
        </div>
        {/* Embed Code */}
        <div>
          <div className="font-semibold theme-text-heading-2 mt-4">
            <I18>Embed Code</I18>
          </div>
          <textarea
            rows="5"
            placeholder="Enter here"
            className="w-full rounded bg-transparent py-2 focus:outline-none theme-bg-default px-2 border theme-border-default flex items-center mt-3"
            onChange={(e) => {
              setEmbedCode(e.target.value);
              // set address
            }}
            value={embedCode}
          />
          <ValidationErrorDiv errorKey="embedCode" errors={errors} />
        </div>
        <div className="text-xs theme-text-subtitle-1 mt-1 border-b pb-2">
          <I18>You can use the following variables</I18> -<br />
          <br />
          {`{{userFullName}}`} - <I18>User Full Name</I18>
          <br />
          {`{{userEmail}}`} - <I18>User Email</I18>
          <br />
          {`{{userId}}`} - <I18>User Id</I18>
          <br />
          {`{{userMobile}}`} - <I18>User Mobile</I18>
          <br />
          {`{{countryCode}}`} - <I18>Country Code</I18>
          <br />
          {`{{communityRole}}`} - <I18>Community Role</I18>
          <br />
          {
            hasAuthTokenEmbedEnabled ? (
              <>
                {`{{authToken}}`} - <I18>User Auth Token</I18>
                <br />
              </>
            ) : <></>
          }
        </div>
        <div
          className="flex cursor-pointer mt-2"
          onClick={() => {
            setIsActive(!isActive);
          }}>
          <div className="mr-2">
            <I18>Visibility</I18>
          </div>
          <Checkbox selected={isActive} />
        </div>
        {/* error div */}
        {error && (
          <div className="mt-3">
            <ErrorDiv error={error} />
          </div>
        )}
        {/* save button */}
        <div className="flex justify-end mt-3">
          <Button
            onClick={handleSubmit}
            label={selectedEmbeddedSectionId ? "Update" : "Save"}
          />
        </div>
        {/* Preview Area */}
        <div>
          <div className="font-semibold theme-text-heading-2 mt-4">
            <I18>Preview Area</I18>
          </div>
          <div className="border rounded mt-3">
            <EmbeddedSectionView
              embeddedSection={{
                embedCode,
                name,
              }}
            />
          </div>
        </div>
      </div>
      {/* delete modal */}
      <ActionModal
        active={isDeleteModalVisible}
        onSubmit={deleteSection}
        header="Delete Channel"
        setActive={setIsDeleteModalVisible}
        title="Are you sure you want to delete this channel?"
        on
        btnColor="red"
      />
    </div>
  );
}

const CommunitySettingEmbeddedSectionEditPage = withToast(
  connect((s) => ({
    user: s.auth,
    community: s.community,
  }))(CommunitySettingEmbeddedSectionEditPageComponent)
);

export default CommunitySettingEmbeddedSectionEditPage;
